import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { FolderKanban } from 'lucide-react';
import { projectService, type Project } from '@/lib/projectService';
import { ProjectCard } from '@/components/projects/ProjectCard';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

export const PortalProjects: React.FC = () => {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  const loadProjects = useCallback(async () => {
    if (!currentWorkspace || !user) return;
    try {
      setLoading(true);
      const data = await projectService.getProjectsForClient(currentWorkspace.id, user.id);
      setProjects(data);
    } catch (error) {
      console.error('Error loading projects:', error);
      toast.error('Failed to load your projects');
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id, user?.id]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  return (
    <div className="w-full p-6 md:p-10">
      <Helmet>
        <title>My Projects | Client Portal</title>
      </Helmet>

      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2 text-foreground">My Projects</h1>
        <p className="text-muted-foreground">View and manage your active projects</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-muted-foreground border-t-workspace-accent" />
        </div>
      ) : projects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map(project => (
            <div key={project.id} className="relative group">
              {/* Note: The ProjectCard defaults to linking to /boards/projects/:slug. 
                   We will intercept it entirely by wrapping it in our own link that overlays it,
                   OR we can just provide an onClick to redirect.
                   Since ProjectCard is shared, let's wrap it in a clickable invisible div that intercepts clicks to redirect to /projects/:slug */}
              <Link to={`/projects/${project.slug}`} className="absolute inset-0 z-20" />
              <div className="pointer-events-none relative z-10 group-hover:[&_a]:pointer-events-none">
                <ProjectCard project={project} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 bg-card border border-border rounded-xl">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 bg-workspace-accent/10">
            <FolderKanban className="w-10 h-10 text-workspace-accent" />
          </div>
          <h2 className="text-xl font-bold mb-2 text-foreground">No projects yet</h2>
          <p className="text-center max-w-md text-muted-foreground">
            You haven't been assigned to any projects yet. When a project is ready, it will appear here.
          </p>
        </div>
      )}
    </div>
  );
};
